import React, { useState, useEffect, useCallback, useMemo } from 'react';
import permissionService from '../services/permissionService';
import CommonPagination from '../components/common/CommonPagination';
import './TenantAuditTrailPage.css';

export const TenantAuditTrailPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [changeTypeFilter, setChangeTypeFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [limit, setLimit] = useState(100);

  // Client-side pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  const loadAuditLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await permissionService.getAuditTrail({
        changeType: changeTypeFilter || undefined,
        limit
      });
      const data = res?.data || (Array.isArray(res) ? res : []);
      setLogs(data);
      setCurrentPage(1);
    } catch (err) {
      console.error('Failed to load audit logs:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed loading audit trail.');
    } finally {
      setLoading(false);
    }
  }, [changeTypeFilter, limit]);

  useEffect(() => {
    loadAuditLogs();
  }, [loadAuditLogs]);

  // Filtered logs based on search term
  const filteredLogs = useMemo(() => {
    if (!searchTerm.trim()) return logs;
    const q = searchTerm.toLowerCase();
    return logs.filter((log) => {
      const actor = (log.modifiedByName || '').toLowerCase();
      const emp = (log.employeeName || '').toLowerCase();
      const role = (log.roleName || '').toLowerCase();
      const screen = (log.screenName || log.screenCode || '').toLowerCase();
      const change = (log.changeType || '').toLowerCase();
      const val = (log.newValue || '').toLowerCase();
      return (
        actor.includes(q) ||
        emp.includes(q) ||
        role.includes(q) ||
        screen.includes(q) ||
        change.includes(q) ||
        val.includes(q)
      );
    });
  }, [logs, searchTerm]);

  // Statistics
  const stats = useMemo(() => {
    const roleChanges = logs.filter(l => l.changeType?.includes('ROLE')).length;
    const userOverrides = logs.filter(l => l.changeType?.includes('OVERRIDE')).length;
    const workspaceAssignments = logs.filter(l => l.changeType?.includes('WORKSPACE')).length;
    return {
      total: logs.length,
      roleChanges,
      userOverrides,
      workspaceAssignments
    };
  }, [logs]);

  // Paginated records
  const totalPages = Math.max(1, Math.ceil(filteredLogs.length / pageSize));
  const paginatedLogs = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredLogs.slice(start, start + pageSize);
  }, [filteredLogs, currentPage, pageSize]);

  const getBadgeClass = (changeType = '') => {
    const c = changeType.toUpperCase();
    if (c.includes('GRANT') || c.includes('ALLOW') || c.includes('CREATE')) return 'audit-badge-success';
    if (c.includes('REVOKE') || c.includes('DENY') || c.includes('DELETE') || c.includes('LOCK')) return 'audit-badge-danger';
    if (c.includes('OVERRIDE') || c.includes('UPDATE')) return 'audit-badge-warning';
    return 'audit-badge-primary';
  };

  return (
    <div className="tenant-audit-container">
      {/* Header */}
      <div className="tenant-audit-header">
        <div className="tenant-audit-header-title">
          <h1>
            <span>📜</span> Enterprise Security Audit Trail
          </h1>
          <p>Immutable record of authentication, RBAC authorizations, user overrides, and security events</p>
        </div>
        <div>
          <button
            className="tenant-audit-btn tenant-audit-btn-primary"
            onClick={loadAuditLogs}
            disabled={loading}
          >
            {loading ? '⏳ Refreshing...' : '🔄 Refresh Logs'}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="tenant-audit-stats-grid">
        <div className="tenant-audit-stat-card">
          <span className="tenant-audit-stat-label">Total Audit Events</span>
          <div className="tenant-audit-stat-value">{stats.total}</div>
          <div className="tenant-audit-stat-sub">Recorded in active audit trail</div>
        </div>
        <div className="tenant-audit-stat-card">
          <span className="tenant-audit-stat-label">Role Policy Changes</span>
          <div className="tenant-audit-stat-value" style={{ color: '#0284c7' }}>
            {stats.roleChanges}
          </div>
          <div className="tenant-audit-stat-sub">Matrix modifications</div>
        </div>
        <div className="tenant-audit-stat-card">
          <span className="tenant-audit-stat-label">User Permission Overrides</span>
          <div className="tenant-audit-stat-value" style={{ color: '#d97706' }}>
            {stats.userOverrides}
          </div>
          <div className="tenant-audit-stat-sub">Individual grant/revoke overrides</div>
        </div>
        <div className="tenant-audit-stat-card">
          <span className="tenant-audit-stat-label">Workspace Changes</span>
          <div className="tenant-audit-stat-value" style={{ color: '#059669' }}>
            {stats.workspaceAssignments}
          </div>
          <div className="tenant-audit-stat-sub">Centre and role reassignments</div>
        </div>
      </div>

      {/* Audit Log Card */}
      <div className="tenant-audit-card">
        <div className="tenant-audit-card-header">
          <h3>Audit Log Stream</h3>
          <div className="tenant-audit-toolbar">
            <input
              type="text"
              className="tenant-audit-search-input"
              placeholder="Search by operator, employee, role, or action..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              className="tenant-audit-select"
              value={changeTypeFilter}
              onChange={(e) => setChangeTypeFilter(e.target.value)}
            >
              <option value="">All Event Types</option>
              <option value="ROLE_MATRIX_UPDATE">Role Matrix Updates</option>
              <option value="USER_OVERRIDE">User Overrides</option>
              <option value="WORKSPACE_ASSIGNMENT">Workspace Assignments</option>
              <option value="ROLE_CREATED">Role Creation</option>
            </select>
            <select
              className="tenant-audit-select"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
            >
              <option value={50}>50 records</option>
              <option value={100}>100 records</option>
              <option value={200}>200 records</option>
            </select>
          </div>
        </div>

        {error && (
          <div style={{ padding: '1rem', background: '#fef2f2', borderBottom: '1px solid #fecaca', color: '#b91c1c', fontSize: '0.875rem' }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        <div style={{ overflowX: 'auto' }}>
          <table className="tenant-audit-table">
            <thead>
              <tr>
                <th>Event ID</th>
                <th>Timestamp</th>
                <th>Change Type</th>
                <th>Target Scope</th>
                <th>Subject / Screen</th>
                <th>Previous → New</th>
                <th>Modified By</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
                    ⏳ Loading enterprise security audit logs...
                  </td>
                </tr>
              ) : paginatedLogs.length > 0 ? (
                paginatedLogs.map((log) => (
                  <tr key={log.auditId || Math.random()}>
                    <td style={{ fontFamily: 'monospace', fontWeight: 600, color: '#64748b' }}>
                      #{log.auditId}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', color: '#475569', fontSize: '0.75rem' }}>
                      {log.modifiedDate ? new Date(log.modifiedDate).toLocaleString() : '—'}
                    </td>
                    <td>
                      <span className={`audit-badge ${getBadgeClass(log.changeType)}`}>
                        {log.changeType || 'EVENT'}
                      </span>
                    </td>
                    <td>
                      {log.roleName ? (
                        <span>Role: <strong>{log.roleName}</strong></span>
                      ) : log.employeeName ? (
                        <span>User: <strong>{log.employeeName}</strong></span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>Global</span>
                      )}
                      {log.centreName && (
                        <div style={{ fontSize: '0.7rem', color: '#64748b' }}>📍 {log.centreName}</div>
                      )}
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>
                        {log.screenName || log.screenCode || '—'}
                      </div>
                      {log.actionCode && (
                        <div style={{ fontSize: '0.7rem', color: '#0284c7', fontFamily: 'monospace' }}>
                          [{log.actionCode}]
                        </div>
                      )}
                    </td>
                    <td style={{ maxWidth: '280px', wordBreak: 'break-word', fontSize: '0.75rem' }}>
                      {log.previousValue != null && (
                        <span style={{ color: '#94a3b8', textDecoration: 'line-through', marginRight: '0.5rem' }}>
                          {log.previousValue}
                        </span>
                      )}
                      <span style={{ fontWeight: 600, color: '#0f172a' }}>
                        {log.newValue || '—'}
                      </span>
                    </td>
                    <td>
                      <strong>{log.modifiedByName || 'System'}</strong>
                      {log.modifiedById && (
                        <span style={{ fontSize: '0.7rem', color: '#94a3b8', marginLeft: '0.25rem' }}>
                          (ID: {log.modifiedById})
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
                    No audit records matching criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && filteredLogs.length > pageSize && (
          <div style={{ padding: '0.75rem 1.25rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, filteredLogs.length)} of {filteredLogs.length} events
            </span>
            <CommonPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TenantAuditTrailPage;
